import React, { useCallback, useContext, useEffect, useState } from 'react';
import c from 'classnames';
import Script from 'react-load-script';
import logoD from './dumpert-d.svg';
import styles from './Ads.css';
import tempAdStyles from './AdsTempContainer.css';
import { settings } from '../../settings';
import { useDispatch, useSelector } from 'react-redux';
import { AdsBlockerModal } from '../modals';
import { AdBlockerUserWarning } from '../../adblkcheckerscript';
import {
  adsDefineSlotOp,
  adsDestroySlotOp,
  adsInitOp,
  selectKachingInstance,
  selectItemTags,
  selectIsCardsView,
  selectItemMedia,
  selectDidomiConsentCookieAcceptedOrSkipped,
  selectItemId, 
  adsSetKachingConfig,
  selectIsLoggedIn,
} from '../../ducks';
import { documentGetCookie } from '../../cookie';
import { Device, Browser } from '../../device';
import { AdsTempContainer } from './';


export const createSlotParams = (
  adType, 
  adNumber,
  showPlaceholder = false, 
  isVideoCompanion = false,
  showTempContent = false,
) => ({
  adType,
  adNumber,
  adOptions: {
    showPlaceholder,
    isVideoCompanion,
    showTempContent,
  },
});

export const getIABConsentString = () => {
  if(typeof window !== 'object') {
    return '';
  }
  if(window.didomiState && window.didomiState.didomiIABConsent) {
    return window.didomiState.didomiIABConsent;
  }
  const cookieName = 'euconsent-v2';
  let item = documentGetCookie(cookieName);
  if (item) {
    return item;
  }
  try {
    item = window.localStorage.getItem(cookieName);
  } catch (e) { /* no-op */ }

  return item;
};

export const AdContext = React.createContext(null);

export const AdScript = ({ adunit, children, blockads = true, isModal = false }) => {
  const didomiConsentCookieAcceptedOrSkipped = useSelector(selectDidomiConsentCookieAcceptedOrSkipped);
  const kaching = useSelector(selectKachingInstance);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isCardsView = useSelector(selectIsCardsView);
  const dispatch = useDispatch();
  const itemTags = (useSelector(selectItemTags) || '').split(' ');
  const itemMedia = useSelector(selectItemMedia) || [];
  const itemId = useSelector(selectItemId);
  const [loaded, setLoaded] = useState(0);
  const [errors, setErrors] = useState(0);
  const adunitFullPath = `dumpert.nl/${settings.ADS_SCRIPT.includes('preview') ? 'preview' : adunit}`;

  const onLoad = useCallback(() => setLoaded(x => x + 1), []);
  const onError = useCallback(() => setErrors(x => x + 1), []);

  useEffect(() => {
    if (blockads) {
      return;
    }
    dispatch(adsSetKachingConfig({
      adunit: adunitFullPath,
      blockads: false,
      video: adunit === 'item' ? itemMedia.some(x => x.mediatype === 'VIDEO') : false,
      page: {
        taglist: itemTags,
      },
      personalAds: true,
      targeting: {
        browser_name: Browser.NAME,
        viewMode: isCardsView ? 'card' : 'list',
        page_type: adunit === 'item' ? 'detail' : 'home',
        article_taglist: itemTags, 
        logged_in: isLoggedIn ? 'registered' : 'false',
      },
      lazyload: {
        fetchMarginPercent: Device.IS_DESKTOP ? 50 : 25,
        renderMarginPercent: Device.IS_DESKTOP ? 25: 10,
        scatterSlots: true,
      },
      videoTargeting: {
        iabconsentstring: getIABConsentString(),
        ...(function (isItemPage, itemMedia) {

          if(isItemPage && itemMedia.some(x => x.mediatype === 'VIDEO')) {
            const getDurationType = duration => {
              if (duration > 0 && duration < 30) { return 'clip'; }
              if (duration > 29 && duration < 60) { return 'short'; }
              if (duration > 59 && duration < 180) { return 'medium'; }
              if (duration > 181 && duration < 301) { return 'long'; }
              if (duration > 301) { return 'extra long'; }
            };
            const videoItemDuration = itemMedia.find(x => x.mediatype === 'VIDEO').duration;

            return ({
              vidduration: videoItemDuration,
              viddurationtype: getDurationType(videoItemDuration),
            });
          }
        }(adunit === 'item', itemMedia)),
      },
    }));
  }, [blockads, adunit, adunitFullPath, JSON.stringify(itemMedia), itemTags.join(), isCardsView]);

  useEffect(() => {
    if (loaded + errors === 2 && !kaching && !blockads) {
      dispatch(adsInitOp());
    }
  }, [loaded + errors, kaching, blockads]);

  return (
    <>
      {!blockads && didomiConsentCookieAcceptedOrSkipped &&
      <Script url={`${settings.ADS_SCRIPT_2}`} onLoad={onLoad} onError={onError} 
        attributes={{ 'data-uid': documentGetCookie('dataAttribute')}}/>}
      {!blockads && didomiConsentCookieAcceptedOrSkipped &&
      <Script url={`${settings.ADS_SCRIPT}`} onLoad={onLoad} onError={onError}/>}
      <AdContext.Provider value={{
        blockads,
        isModal,
        itemId,
        adunit: adunitFullPath,
      }}>
        {children}
      </AdContext.Provider>
      <AdBlockerUserWarning>
        <AdsBlockerModal />
      </AdBlockerUserWarning>
    </>
  );
};

export const Ads = ({ adType, adNumber, adOptions }) => {

  const { adunit, blockads, isModal, itemId } = useContext(AdContext);
  const dispatch = useDispatch();
  const adNumberWithPrefix = `${isModal ? 'detail' : 'overview'}-${adNumber}`;

  useEffect(() => {
    if (!blockads) {
      dispatch(adsDefineSlotOp({ adType, adNumber: adNumberWithPrefix, adunit, adOptions, itemId }));
      return () => dispatch(adsDestroySlotOp({ adType, adNumber: adNumberWithPrefix, itemId }));
    }
  }, [
    adType,
    adNumber,
    adunit,
    blockads,
    isModal,
    adOptions.showPlaceholder,
    adOptions.isVideoCompanion,
    adOptions.showTempContent,
    itemId,
  ]);

  const isHomeAd = adunit !== 'item' && adOptions.showTempContent;
  const isHomeTopAd = isHomeAd && adNumber === 't1';

  return (
    <>
      {adType !== 'native' && <div className={c(
        styles.ads_container,
        'grid_ad_container',
        styles[adType],
        { [styles.ad_placeholder]: adOptions.showPlaceholder },
        { [tempAdStyles.ad_placeholder_home]: isHomeAd },
        { [tempAdStyles.top_ad]: isHomeTopAd },        
      )}
      style={!isHomeTopAd ? { backgroundImage: `url('${logoD}')` } : null}
      >
        <div className={c(styles.ad, `adstyle-${adType}`)}>
          <div
            className={`ad ad--${adType}`}
            id={`ad_${adType}${adNumber ? `-${adNumberWithPrefix}` : ''}`}>
          </div>
        </div>
        {isHomeTopAd && <AdsTempContainer />}        
      </div>
      } 

      {adType === 'native' &&
        <div className={`ad ad--${adType}`} id={`ad_${adType}${adNumber ? `-${adNumberWithPrefix}` : ''}`}></div>
      }
    </>
  );
};
